import { quintOut, cubicOut } from 'svelte/easing'
import type { EasingFunction } from 'svelte/transition'

export interface TransitionConfig {
	delay?: number
	duration?: number
	easing?: EasingFunction
}

export interface StaggerConfig extends TransitionConfig {
	index?: number
}

export const slideWithFade = (
	node: HTMLElement,
	{ delay = 0, duration = 400, easing = quintOut }: TransitionConfig = {}
) => {
	const style = getComputedStyle(node)
	const opacity = +style.opacity
	const height = parseFloat(style.height) || node.offsetHeight
	const paddingTop = parseFloat(style.paddingTop)
	const paddingBottom = parseFloat(style.paddingBottom)
	const marginTop = parseFloat(style.marginTop)
	const marginBottom = parseFloat(style.marginBottom)

	return {
		delay,
		duration,
		easing,
		css: (t: number) => {
			return `
        overflow: hidden;
        opacity: ${t * opacity};
        height: ${t * height}px;
        padding-top: ${t * paddingTop}px;
        padding-bottom: ${t * paddingBottom}px;
        margin-top: ${t * marginTop}px;
        margin-bottom: ${t * marginBottom}px;
        transform: translateY(${(1 - t) * -10}px);
      `
		}
	}
}

export const scaleFade = (
	node: Element,
	{ duration = 300, delay = 0, baseScale = 0.5, easing = cubicOut } = {}
) => {
	return {
		duration,
		easing,
		delay,
		css: (t: number) => {
			const scale = baseScale + (1 - baseScale) * t
			return `
				opacity: ${t};
				transform: scale(${scale});
			`
		}
	}
}

export const staggeredFadeIn = (
	node: HTMLElement,
	{ delay = 0, duration = 300, index = 0, easing = cubicOut }: StaggerConfig = {}
) => {
	return {
		delay: delay + index * 50,
		duration,
		easing,
		css: (t: number) => {
			return `
        opacity: ${t};
        transform: translateY(${(1 - t) * 20}px) scale(${0.95 + t * 0.05});
      `
		}
	}
}

// Additional utility transitions you might need
export const slideUp = (
	node: HTMLElement,
	{ delay = 0, duration = 300, easing = quintOut }: TransitionConfig = {}
) => {
	return {
		delay,
		duration,
		easing,
		css: (t: number) => `
      transform: translateY(${(1 - t) * 100}%);
      opacity: ${t};
    `
	}
}

export const scaleIn = (
	node: HTMLElement,
	{ delay = 0, duration = 200, easing = cubicOut }: TransitionConfig = {}
) => {
	return {
		delay,
		duration,
		easing,
		css: (t: number) => `
      transform: scale(${t});
      opacity: ${t};
    `
	}
}

export function expand(node: Element, { duration = 200, delay = 0, easing = cubicOut } = {}) {
	const height = node.scrollHeight
	return {
		duration,
		delay,
		easing,
		css: (t: number) => `height: ${t * height}px; overflow: hidden;`
	}
}

export function fadeSlide(
	node: Element,
	{ duration = 200, x = -120, y = 0, delay = 0, easing = cubicOut } = {}
) {
	return {
		duration,
		delay,
		easing,
		css: (t: number) => `
			opacity: ${t};
			transform: translate(${(1 - t) * x}px, ${(1 - t) * y}px);
			`
	}
}
export function fadeHeight(
	node: Element,
	{ duration = 250, delay = 0, easing = (t: number) => 1 - Math.pow(1 - t, 3) } = {}
) {
	const style = getComputedStyle(node)
	const height = parseFloat(style.height)
	const opacity = +style.opacity || 1

	return {
		delay,
		duration,
		css: (t: number) => {
			const eased = easing(t)
			return `
				overflow: hidden;
				height: ${eased * height}px;
				opacity: ${eased * opacity};
			`
		}
	}
}

export function scaleWidth(node: Element, { duration = 300 } = {}) {
	const style = getComputedStyle(node)
	const width = parseFloat(style.width)

	return {
		duration,
		easing: cubicOut,
		css: (t: number) => `
				transform: scale(${t});
				width: ${t * width}px;
				opacity: ${t};
				overflow: hidden;
			`
	}
}

export function chatMessageAppear(node: Element, params?: any) {
	const { duration = params?.animationDuration ?? 500, delay = 0 } = params || {}

	return {
		duration,
		delay,
		easing: quintOut,
		css: (t: number) => {
			const opacity = t
			const scale = 0.95 + t * 0.05
			const y = -(1 - t) * 16

			// Blur starts at 6px and goes to 0 as it appears
			const blur = (1 - t) * 6

			return `
				opacity: ${opacity};
				transform: scale(${scale}) translateY(${y}px);
				filter: blur(${blur}px);
			`
		}
	}
}
